import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';

/**
 * URL遷移やホスト名の解決等を行うモジュール
 */
export interface CustomWindow extends Window {
  sasApiUrl: string;
  sasStaticUrl: string;
  REACT_APP_BASE_URL: string;
  canonIdApiUrl: string;
}

/**
 * SASのUIのPath
 */
export const SasPagePath = {
  PlanList: '/store/plan',
  PlanDetail: '/store/plan/detail',
  Purchase: '/store/purchase',
  Completion: '/store/completion',
  DeleteCompletion: '/store/deletecompletion',
  Error: '/store/error'
} as const;
type SasPagePath = typeof SasPagePath[keyof typeof SasPagePath];

const sasApiUrl = process.env.REACT_APP_SAS_API_URL;
const sasStaticUrl = process.env.REACT_APP_SAS_STATIC_URL;
const appBaseUrl = process.env.REACT_APP_BASE_URL;
const canonIdApiUrl = process.env.REACT_APP_CANONID_API_URL;

/** CanonIDのログインページへの遷移 */
const canonIdLoginUrl = `${sasApiUrl}/v1/api/login?redirectUrl=${window.location.href}&clientType=browser`;
/** CanonIDのログアウト(プラン一覧画面へリダイレクト) */
const redirectPlanListUrl = encodeURIComponent(
  sasApiUrl + SasPagePath.PlanList
);
const canonIdLogoutUrl = `${canonIdApiUrl}/canonid/account/fe/authn/client-logout?redirect_url=${redirectPlanListUrl}`;
const canonIdProfileUrl = `${canonIdApiUrl}/canonid/#/account`;
/** SASのエラー画面のURL */
const sasErrorUrl = sasApiUrl + SasPagePath.Error;

export {
  sasApiUrl,
  sasStaticUrl,
  appBaseUrl,
  canonIdLoginUrl,
  canonIdLogoutUrl,
  canonIdProfileUrl,
  sasErrorUrl
};

/**
 * クエリパラメータを作成する
 * @param params Keyにクエリパラメータのキー、Valueにクエリパラメータの値をもつオブジェクト
 * @returns クエリパラメータ文字列
 */
export const createQuery = (
  params: { key: string; value: string | number | undefined | null }[]
): string => {
  const filtered = params.filter((p) => p.value);
  return filtered.length === 0
    ? ''
    : '?' + filtered.map((p) => `${p.key}=${p.value}`).join('&');
};

/**
 * プラン一覧URLを作成する
 * @param なし
 * @returns CookieにサービスIDキーとmodelNameがあったらプラン一覧URLにクエリパラメータでサービスIDとmodelを付与したもの、サービスIDとtypeがあったらプラン一覧URLにクエリパラメータでサービスIDとtypeを付与したもの、サービスIDのみあったらプラン一覧URLにクエリパラメータでサービスIDを付与したもの、modelNameのみあったらプラン一覧URLにクエリパラメータでmodelを付与したもの、typeのみあったらプラン一覧URLにクエリパラメータでtypeを付与したもの、どれもなければプラン一覧URLを返す
 */
export const getPlanListUrl = () => {
  const cookie = loadCookie();
  const serviceId = cookie[SasCookieKeys.KEY_SERVICE_ID];
  const modelName = cookie[SasCookieKeys.KEY_MODEL_NAME];
  const type = cookie[SasCookieKeys.KEY_TYPE];

  if (serviceId && modelName) {
    return `${SasPagePath.PlanList}?serviceId=${serviceId}&model=${modelName}`;
  } else if (serviceId && type) {
    return `${SasPagePath.PlanList}?serviceId=${serviceId}&type=${type}`;
  } else if (serviceId) {
    return `${SasPagePath.PlanList}?serviceId=${serviceId}`;
  } else if (modelName) {
    return `${SasPagePath.PlanList}?model=${modelName}`;
  } else if (type) {
    return `${SasPagePath.PlanList}?type=${type}`;
  } else {
    return `${SasPagePath.PlanList}`;
  }
};

/**
 * プラン一覧URLを作成する（初回アクセス画面、国地域選択時に使用）
 * @param serviceId
 * @returns 引数があったらプラン一覧URLにクエリパラメータでサービスIDを付与したもの、
 * なかったらプラン一覧URLを返す
 */
export const getPlanListUrlChangeCountry = (serviceId: string | null) => {
  return !serviceId
    ? `${SasPagePath.PlanList}`
    : `${SasPagePath.PlanList}?serviceId=${serviceId}`;
};

/**
 * 利用中のプラン一覧URLを作成する
 * @param なし
 * @returns CookieにmodelNameキーがあったら利用中のプラン一覧URLにクエリパラメータでmodelを付与したURL、typeキーがあったら利用中のプラン一覧URLにクエリパラメータでtypeを付与したURL、なかったら利用中のプラン一覧URLを返す
 */
export const getPurchaseUrl = () => {
  const cookie = loadCookie();
  const modelName = cookie[SasCookieKeys.KEY_MODEL_NAME];
  const type = cookie[SasCookieKeys.KEY_TYPE];

  if (modelName) {
    return `${SasPagePath.Purchase}?model=${modelName}`;
  } else if (type) {
    return `${SasPagePath.Purchase}?type=${type}`;
  } else {
    return `${SasPagePath.Purchase}`;
  }
};
